'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';

const legalMatters = [
  'Corporate & Commercial',
  'Property & Real Estate',
  'Litigation & Dispute Resolution',
  'Arbitration / ADR',
  'Family & Matrimonial',
  'Divorce',
  'Fintech & Financial Technology',
  'Technology & Digital Law',
  'Entertainment Law',
  'Oil & Gas / Energy',
  'Governance & Regulatory',
  'Business Advisory',
  'Employment / Labour',
  'Contractual Matters',
  'Other',
];

const urgencyOptions = [
  { value: 'standard', label: 'Standard — Within normal business timelines' },
  { value: 'priority', label: 'Priority — Requires prompt attention' },
  { value: 'urgent', label: 'Urgent — Time-sensitive matter' },
];

const contactMethods = [
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone Call' },
  { value: 'whatsapp', label: 'WhatsApp' },
];

interface FormData {
  fullName: string;
  phone: string;
  email: string;
  legalMatter: string;
  description: string;
  urgency: string;
  preferredContact: string;
  location: string;
  additionalNotes: string;
  consent: boolean;
}

export default function RequestForm() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [requestId, setRequestId] = useState('');
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    phone: '',
    email: '',
    legalMatter: '',
    description: '',
    urgency: 'standard',
    preferredContact: 'email',
    location: '',
    additionalNotes: '',
    consent: false,
  });
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});

  const validate = () => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};
    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Valid email address is required';
    if (!formData.legalMatter) newErrors.legalMatter = 'Please select a legal matter type';
    if (!formData.description.trim() || formData.description.length < 20) newErrors.description = 'Please provide a brief description (at least 20 characters)';
    if (!formData.consent) newErrors.consent = 'Please acknowledge the consent statement';
    return newErrors;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name as keyof FormData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    try {
      const supabase = createClient();
      // Generate reference number
      const refNum = `EAC-${Date.now().toString().slice(-8)}`;
      const description = formData.additionalNotes
        ? `${formData.description}\n\nAdditional Notes: ${formData.additionalNotes}`
        : formData.description;
      const { error: dbError } = await supabase.from('service_requests').insert({
        request_number: refNum,
        client_name: formData.fullName,
        client_email: formData.email,
        client_phone: formData.phone,
        legal_category: formData.legalMatter,
        description,
        urgency: formData.urgency,
        preferred_contact: formData.preferredContact,
        location: formData.location || null,
        status: 'new',
      });
      if (dbError) throw dbError;
      setRequestId(refNum);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      setSubmitError('There was a problem submitting your request. Please try again or contact us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-24 text-center">
        <div className="border border-border bg-card p-12 md:p-16">
          <div className="w-12 h-12 border border-primary flex items-center justify-center mx-auto mb-8">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" className="text-primary" aria-hidden="true">
              <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div className="flex items-center gap-4 justify-center mb-6">
            <div className="w-8 h-px bg-primary" />
            <span className="text-xs tracking-widest uppercase text-muted-foreground">Request Received</span>
            <div className="w-8 h-px bg-primary" />
          </div>
          <h2 className="font-display text-3xl italic text-foreground mb-4">
            Your Legal Request Has Been Received
          </h2>
          <p className="text-muted-foreground leading-relaxed mb-6 max-w-lg mx-auto">
            Your request has been securely submitted to Your Law Firm. The appropriate team will review your matter and be in contact through your preferred channel.
          </p>
          <div className="bg-muted border border-border px-6 py-4 inline-block mb-8">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Reference Number</p>
            <p className="font-display text-2xl text-primary font-light">{requestId}</p>
          </div>
          <p className="text-xs text-muted-foreground mb-10 leading-relaxed max-w-md mx-auto">
            Please retain your reference number for your records. This does not constitute legal advice or create a solicitor-client relationship at this stage.
          </p>
          <Link
            href="/"
            className="inline-flex items-center gap-3 text-xs font-semibold uppercase tracking-widest bg-primary text-primary-foreground px-8 py-4 hover:bg-accent transition-colors"
          >
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
      <div className="border border-border bg-card p-1">
        <div className="border border-border/50 p-8 md:p-12">
          {/* Progress indicator */}
          <div className="flex items-center gap-3 mb-12">
            <span className="text-xs text-foreground font-mono">01</span>
            <div className="h-px flex-1 bg-border">
              <div className="h-full w-1/4 bg-primary" />
            </div>
            <span className="text-xs text-muted-foreground font-mono">04</span>
          </div>

          <form onSubmit={handleSubmit} noValidate>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              {/* Left column */}
              <div className="space-y-12">
                {/* Personal Information */}
                <div>
                  <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-8 font-semibold">
                    Personal Information
                  </label>
                  <div className="space-y-8">
                    <div>
                      <input
                        type="text"
                        name="fullName"
                        placeholder="Full Name *"
                        value={formData.fullName}
                        onChange={handleChange}
                        className="form-input"
                        autoComplete="name"
                        aria-label="Full Name"
                      />
                      {errors.fullName && <p className="text-xs text-red-400 mt-2">{errors.fullName}</p>}
                    </div>
                    <div>
                      <input
                        type="tel"
                        name="phone"
                        placeholder="Phone Number *"
                        value={formData.phone}
                        onChange={handleChange}
                        className="form-input"
                        autoComplete="tel"
                        aria-label="Phone Number"
                      />
                      {errors.phone && <p className="text-xs text-red-400 mt-2">{errors.phone}</p>}
                    </div>
                    <div>
                      <input
                        type="email"
                        name="email"
                        placeholder="Email Address *"
                        value={formData.email}
                        onChange={handleChange}
                        className="form-input"
                        autoComplete="email"
                        aria-label="Email Address"
                      />
                      {errors.email && <p className="text-xs text-red-400 mt-2">{errors.email}</p>}
                    </div>
                    <div>
                      <input
                        type="text"
                        name="location"
                        placeholder="Location / City (optional)"
                        value={formData.location}
                        onChange={handleChange}
                        className="form-input"
                        aria-label="Location"
                      />
                    </div>
                  </div>
                </div>

                {/* Preferred Contact */}
                <div>
                  <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-6 font-semibold">
                    Preferred Contact Method
                  </label>
                  <div className="space-y-3">
                    {contactMethods.map((method) => (
                      <label key={method.value} className="flex items-center gap-4 cursor-pointer group">
                        <input
                          type="radio"
                          name="preferredContact"
                          value={method.value}
                          checked={formData.preferredContact === method.value}
                          onChange={handleChange}
                          className="sr-only"
                        />
                        <div className={`w-4 h-4 border flex items-center justify-center transition-colors duration-200 ${formData.preferredContact === method.value ? 'border-primary' : 'border-border'}`}>
                          {formData.preferredContact === method.value && (
                            <div className="w-2 h-2 bg-primary" />
                          )}
                        </div>
                        <span className="text-sm text-foreground group-hover:text-primary transition-colors">{method.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right column */}
              <div className="space-y-12">
                {/* Legal Matter */}
                <div>
                  <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-8 font-semibold">
                    Legal Matter
                  </label>
                  <div className="space-y-8">
                    <div className="relative">
                      <select
                        name="legalMatter"
                        value={formData.legalMatter}
                        onChange={handleChange}
                        className="form-select"
                        aria-label="Type of Legal Assistance"
                      >
                        <option value="">Type of Legal Assistance *</option>
                        {legalMatters.map((m) => (
                          <option key={m} value={m}>{m}</option>
                        ))}
                      </select>
                      <div className="absolute right-0 top-3 pointer-events-none text-muted-foreground" aria-hidden="true">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24">
                          <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="m6 9l6 6l6-6" />
                        </svg>
                      </div>
                      {errors.legalMatter && <p className="text-xs text-red-400 mt-2">{errors.legalMatter}</p>}
                    </div>

                    <div>
                      <textarea
                        name="description"
                        placeholder="Brief Description of the Matter *"
                        value={formData.description}
                        onChange={handleChange}
                        rows={5}
                        className="form-input resize-none"
                        aria-label="Brief Description"
                      />
                      {errors.description && <p className="text-xs text-red-400 mt-2">{errors.description}</p>}
                    </div>

                    <div>
                      <textarea
                        name="additionalNotes"
                        placeholder="Additional Notes (optional)"
                        value={formData.additionalNotes}
                        onChange={handleChange}
                        rows={3}
                        className="form-input resize-none"
                        aria-label="Additional Notes"
                      />
                    </div>
                  </div>
                </div>

                {/* Urgency */}
                <div>
                  <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-6 font-semibold">
                    Urgency
                  </label>
                  <div className="space-y-3">
                    {urgencyOptions.map((option) => (
                      <label key={option.value} className="flex items-start gap-4 cursor-pointer group">
                        <input
                          type="radio"
                          name="urgency"
                          value={option.value}
                          checked={formData.urgency === option.value}
                          onChange={handleChange}
                          className="sr-only"
                        />
                        <div className={`w-4 h-4 border flex-shrink-0 mt-0.5 flex items-center justify-center transition-colors duration-200 ${formData.urgency === option.value ? 'border-primary' : 'border-border'}`}>
                          {formData.urgency === option.value && (
                            <div className="w-2 h-2 bg-primary" />
                          )}
                        </div>
                        <span className="text-sm text-foreground group-hover:text-primary transition-colors leading-tight">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Consent */}
            <div className="mt-12 pt-12 border-t border-border">
              <label className="flex items-start gap-4 cursor-pointer group">
                <input
                  type="checkbox"
                  name="consent"
                  checked={formData.consent}
                  onChange={handleChange}
                  className="sr-only"
                />
                <div className={`w-5 h-5 border flex-shrink-0 mt-0.5 flex items-center justify-center transition-colors duration-200 ${formData.consent ? 'border-primary bg-primary' : 'border-border'}`}>
                  {formData.consent && (
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" className="text-primary-foreground" aria-hidden="true">
                      <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
                <span className="text-sm text-muted-foreground leading-relaxed">
                  I understand that submitting this request does not create a solicitor-client relationship and does not constitute legal advice. I consent to Your Law Firm processing my personal information for the purpose of reviewing and responding to my legal assistance request, in accordance with their{' '}
                  <Link href="/privacy-policy" className="text-primary hover:text-accent transition-colors underline">Privacy Policy</Link>.
                </span>
              </label>
              {errors.consent && <p className="text-xs text-red-400 mt-3 ml-9">{errors.consent}</p>}
            </div>

            {/* Submit error */}
            {submitError && (
              <div className="mt-6 bg-red-950/30 border border-red-900/40 text-red-400 text-xs px-4 py-3">
                {submitError}
              </div>
            )}

            {/* Submit */}
            <div className="mt-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <p className="text-xs text-muted-foreground max-w-sm leading-relaxed">
                Your information is transmitted securely. Submission of this form is the first step in the engagement process.
              </p>
              <button
                type="submit"
                disabled={submitting}
                className="w-full md:w-auto px-12 py-4 bg-primary hover:bg-accent text-primary-foreground text-xs font-semibold uppercase tracking-widest transition-all duration-300 flex items-center justify-center gap-3 group disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? 'Submitting…' : 'Submit Legal Request'}
                {!submitting && (
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" className="group-hover:translate-x-1 transition-transform" aria-hidden="true">
                    <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M5 12h14m-7-7l7 7l-7 7" />
                  </svg>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}